/** The viewer's network relationship with another user, derived from
 *  network_requests rows the viewer can see (RLS = rows they're a party to). */
export type NetworkStatus = 'none' | 'pending_out' | 'pending_in' | 'declined' | 'linked';

export interface NetworkRequestRow {
  id: string;
  requester_id: string;
  addressee_id: string;
  status: 'pending' | 'accepted' | 'declined';
}

/** Find the (single, directionless-unique) request row between two users. */
export function findPairRequest<T extends NetworkRequestRow>(
  myId: string,
  otherId: string,
  requests: T[]
): T | undefined {
  return requests.find(
    r =>
      (r.requester_id === myId && r.addressee_id === otherId) ||
      (r.requester_id === otherId && r.addressee_id === myId)
  );
}

export function deriveNetworkStatus(
  myId: string,
  otherId: string,
  requests: NetworkRequestRow[]
): NetworkStatus {
  if (!myId || !otherId || myId === otherId) return 'none';
  const request = findPairRequest(myId, otherId, requests);
  if (!request) return 'none';
  if (request.status === 'accepted') return 'linked';
  if (request.status === 'declined') {
    // only the requester sees 'declined' (they may withdraw + re-request);
    // the decliner just sees no pending state
    return request.requester_id === myId ? 'declined' : 'none';
  }
  return request.requester_id === myId ? 'pending_out' : 'pending_in';
}
